import { useEffect, useRef } from 'react';

type Bubble = {
  x: number;
  y: number;
  r: number;
  vy: number;
  wobble: number;
  wobbleSpeed: number;
  hue: number;
};

function createBubble(width: number, height: number, fromBottom = true): Bubble {
  const r = 4 + Math.random() * 22;
  return {
    x: Math.random() * width,
    y: fromBottom ? height + r + Math.random() * 80 : Math.random() * height,
    r,
    vy: 0.6 + Math.random() * 1.8,
    wobble: Math.random() * Math.PI * 2,
    wobbleSpeed: 0.015 + Math.random() * 0.025,
    hue: Math.random() > 0.65 ? 42 : 78,
  };
}

type BubbleCanvasProps = {
  active: boolean;
  burst?: boolean;
};

export function BubbleCanvas({ active, burst = false }: BubbleCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bubblesRef = useRef<Bubble[]>([]);
  const burstRef = useRef(burst);
  burstRef.current = burst;

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    bubblesRef.current = Array.from({ length: 28 }, () => createBubble(width, height));

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (const b of bubblesRef.current) {
        if (burstRef.current) {
          b.r += 2.4;
          b.vy *= 0.92;
        } else {
          b.y -= b.vy;
          b.wobble += b.wobbleSpeed;
          b.x += Math.sin(b.wobble) * 0.35;
        }

        if (b.y + b.r < -40 || b.r > Math.max(width, height)) {
          Object.assign(b, createBubble(width, height));
        }

        const alpha = burstRef.current ? 0.35 : 0.12 + (b.r / 26) * 0.18;
        const grad = ctx.createRadialGradient(b.x - b.r * 0.3, b.y - b.r * 0.3, 0, b.x, b.y, b.r);
        grad.addColorStop(0, `hsla(${b.hue}, 85%, 72%, ${alpha + 0.15})`);
        grad.addColorStop(0.45, `hsla(${b.hue}, 70%, 55%, ${alpha})`);
        grad.addColorStop(1, `hsla(${b.hue}, 60%, 40%, 0)`);

        ctx.beginPath();
        ctx.fillStyle = grad;
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.strokeStyle = `hsla(0, 0%, 100%, ${alpha * 0.35})`;
        ctx.lineWidth = 1;
        ctx.arc(b.x - b.r * 0.25, b.y - b.r * 0.28, b.r * 0.22, 0, Math.PI * 2);
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [active]);

  return (
    <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden />
  );
}
